import { useEffect, useMemo, useState } from 'react'
import { CalendarClock, Eye, Fish, Rocket, Save, Users } from 'lucide-react'
import Button from '../../components/Button'
import DateTimePicker from '../../components/DateTimePicker'
import { FormDrawer } from '../../components/Drawer'
import { FormBody, FormCols, FormSection, FormSpan, SwitchField } from '../../components/Form'
import FormRow from '../../components/FormRow'
import GroupPicker from '../../components/GroupPicker'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { TemplatePreview } from './TemplateSurfaces'
import { previewCampaignAudience } from './phishingService'
import { campaignsStrings as S } from './strings'

const EMPTY = {
  name: '',
  templateId: '',
  url: '',
  senderId: '',
  startDate: '',
  endDate: '',
  sendWindow: '0',
  groupIds: [],
  programId: '',
  isActive: true,
}

const MINUTE_MS = 60_000

/** The sentinel for a stored moment that is none of the offered durations — see below. */
const CUSTOM = 'custom'

/**
 * THE FIELD IS A DURATION; THE API WANTS AN INSTANT.
 *
 * `SendEmailsBy` is an absolute date and time, and the engine spreads the whole audience
 * evenly from the moment the campaign starts until it (see `PhishingCampaignService`). So
 * the only thing the admin was ever deciding was HOW LONG that spread should be — and the
 * old datetime picker made them do the arithmetic to express it, against a start date on
 * another control and three server rules they could not see.
 *
 * The spread runs from when the campaign actually BEGINS, which is its start date, or now
 * if that has already passed — the same instant the worker measures from. Anchoring on the
 * start date alone would put the deadline in the past for a campaign whose start has been
 * and gone, and the server refuses a `SendEmailsBy` that is not in the future.
 *
 * @returns {string} `YYYY-MM-DDTHH:mm`, or '' for "everyone at once", which the API stores
 *   as null and the engine reads as "schedule them all at `now`".
 */
function sendEmailsByFor(startDate, sendWindow, customIso = '') {
  if (sendWindow === CUSTOM) return customIso
  const minutes = Number(sendWindow)
  if (!startDate || !minutes) return ''
  const begins = Math.max(new Date(startDate).getTime(), Date.now())
  if (Number.isNaN(begins)) return ''
  const at = new Date(begins + minutes * MINUTE_MS)
  const pad = (n) => String(n).padStart(2, '0')
  return (
    `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}` +
    `T${pad(at.getHours())}:${pad(at.getMinutes())}`
  )
}

/**
 * Reads an existing campaign's stored moment back as one of the durations.
 *
 * A campaign made in the legacy app — or in this form before it changed — can hold any
 * instant at all. Rounding it to the nearest offered duration would silently RESCHEDULE a
 * live campaign the moment someone opened it to fix a typo in its name, so anything that
 * is not one of the durations comes back as itself and is offered as an extra option.
 */
function sendWindowFrom(campaign, options) {
  const stored = campaign?.sendEmailsBy
  if (!stored) return '0'
  if (!campaign?.startDate) return CUSTOM
  const minutes = Math.round((new Date(stored) - new Date(campaign.startDate)) / MINUTE_MS)
  return options.some((option) => Number(option.value) === minutes) ? String(minutes) : CUSTOM
}

/**
 * إضافة جديدة / تعديل — the phishing campaign form.
 *
 * A campaign is four decisions: what it sends (template), where it points
 * (URL + sender), when it runs, and who receives it. They are grouped that way
 * here — the legacy is one flat column of ten controls in a modal, where the
 * scheduling field sits between two unrelated selects.
 *
 * `الإدارات` is a real multi-select rather than the legacy's select2 with a
 * sentinel "تحديد الكل" OPTION — an option that is not a value is a trap: it
 * shows up as a chosen item and posts nothing.
 */
export default function CampaignFormDrawer({
  open,
  campaign,
  cloning = false,
  templates = [],
  domains = [],
  senders = [],
  groups = [],
  programs = [],
  onClose,
  onSave,
  saving = false,
}) {
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewSurface, setPreviewSurface] = useState('email')
  const [review, setReview] = useState(false)
  const [audience, setAudience] = useState(null)
  const [checkingAudience, setCheckingAudience] = useState(false)
  const [reviewError, setReviewError] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [clockMs, setClockMs] = useState(() => Date.now())
  const reviewRequired = cloning || !campaign || !campaign.startedAt

  const selectedTemplate = useMemo(() => {
    if (!values.templateId) return null
    return templates.find((item) => String(item.value) === String(values.templateId)) || null
  }, [templates, values.templateId])
  const launchAction = !values.isActive ? 'draft'
    : Date.parse(audience?.StartAtUtc || '') > clockMs ? 'schedule' : 'activate'
  const actionLabel = S.review[launchAction]
  const ActionIcon = launchAction === 'draft' ? Save : launchAction === 'schedule' ? CalendarClock : Rocket

  useEffect(() => {
    if (!open) return
    /* Null-safe: the spread would otherwise let a null from the row REPLACE the default,
       and `values.name.trim()` / `values.groupIds.length` throw on the first submit. */
    setValues(
      campaign
        ? {
            ...EMPTY,
            ...Object.fromEntries(Object.entries(campaign).filter(([, v]) => v != null)),
            sendWindow: sendWindowFrom(campaign, S.form.sendWindows),
          }
        : EMPTY,
    )
    setErrors({})
    setPreviewOpen(false)
    setReview(false)
    setAudience(null)
    setReviewError('')
    setConfirmed(false)
  }, [open, campaign, cloning])

  useEffect(() => {
    if (!review) return undefined
    const timer = window.setInterval(() => setClockMs(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [review])

  /* If the scheduled instant passes while the review is open, the final action changes
     from scheduling to immediate launch. Require a fresh acknowledgement of that change. */
  useEffect(() => {
    if (!review) return
    setConfirmed(false)
    setReviewError('')
  }, [launchAction, review])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
    setReview(false)
    setAudience(null)
    setConfirmed(false)
  }

  /* What actually goes to the server. Derived at every render rather than stored, so a
     form left open for twenty minutes still posts a deadline in the future. */
  const sendEmailsBy = sendEmailsByFor(values.startDate, values.sendWindow, values.sendEmailsBy)

  /* A campaign whose stored moment is none of the durations keeps it, as its own row. */
  const sendWindowOptions = useMemo(() => (
    values.sendWindow === CUSTOM
      ? [...S.form.sendWindows, {
          value: CUSTOM,
          label: `${S.form.sendWindowKept}: ${String(values.sendEmailsBy).replace('T', ' ')}`,
        }]
      : S.form.sendWindows
  ), [values.sendWindow, values.sendEmailsBy])

  const dateError = useMemo(() => {
    if (!values.startDate || !values.endDate) return null
    return values.endDate < values.startDate ? S.form.required : null
  }, [values.startDate, values.endDate])

  const submit = async (event) => {
    event.preventDefault()
    if (saving || checkingAudience) return
    if (review) {
      if (values.isActive && !confirmed) {
        setReviewError(S.review.confirmRequired)
        return
      }
      const saved = await onSave?.({
        ...values,
        sendEmailsBy,
        name: values.name.trim(),
        audienceFingerprint: audience?.Fingerprint,
        launchAction,
      })
      if (saved === false) {
        setReview(false)
        setAudience(null)
      }
      return
    }
    const next = {}
    if (!values.name.trim()) next.name = S.form.required
    else if (cloning && values.name.trim().toLocaleLowerCase() === campaign?.name?.trim().toLocaleLowerCase()) {
      next.name = S.form.uniqueName
    }
    if (!values.templateId) next.templateId = S.form.required
    if (!values.url) next.url = S.form.required
    if (!values.senderId) next.senderId = S.form.required
    if (!values.startDate) next.startDate = S.form.required
    if (!values.endDate) next.endDate = S.form.required
    if (values.isActive && values.groupIds.length === 0) next.groupIds = S.form.required
    /* This was rendered under the end date and never enforced — a campaign that ends before
       it starts submitted happily, and the server's own guard then rejects it. */
    if (dateError) next.endDate = dateError
    /* The server refuses a deadline past the campaign's end, so a twenty-minute campaign
       cannot spread over three hours. Said here, against the control that decides it. */
    if (reviewRequired && sendEmailsBy) {
      /* A kept moment is the admin's own, from an earlier form — it gets the full check and
         the message written for an instant. A duration can only ever fail one way. */
      if (values.sendWindow === CUSTOM) {
        if (sendEmailsBy <= values.startDate || sendEmailsBy > values.endDate) {
          next.sendWindow = S.review.invalidSendBy
        }
      } else if (values.endDate && sendEmailsBy > values.endDate) {
        next.sendWindow = S.form.windowTooLong
      }
    }
    setErrors(next)
    if (Object.keys(next).length > 0) return
    if (!reviewRequired) {
      onSave?.({ ...values, sendEmailsBy, name: values.name.trim() })
      return
    }
    setCheckingAudience(true)
    setReviewError('')
    try {
      const result = await previewCampaignAudience(values.groupIds, values.startDate)
      if (values.endDate <= result.ServerNowLocal ||
          (sendEmailsBy && sendEmailsBy <= result.ServerNowLocal)) {
        setErrors(values.endDate <= result.ServerNowLocal
          ? { endDate: S.review.invalidEndDate }
          : { sendWindow: S.review.invalidSendBy })
        return
      }
      if (values.isActive && (!result.HasActiveLicense || result.Total === 0)) {
        setErrors({ groupIds: result.HasActiveLicense ? S.review.empty : S.review.noLicense })
        return
      }
      if (values.isActive && result.License && !result.License.CanEnroll) {
        setErrors({ groupIds: result.License.Error === 'campaign_license_capacity'
          ? S.review.capacity.replace('{needed}', result.License.NewSeats).replace('{remaining}', result.License.RemainingSeats ?? 0)
          : S.review.noLicense })
        return
      }
      setAudience(result)
      setConfirmed(false)
      setReview(true)
    } catch {
      setReviewError(S.review.previewFailed)
    } finally {
      setCheckingAudience(false)
    }
  }

  const groupCounts = new Map((audience?.ByGroup ?? []).map((item) => [Number(item.GroupId), item.Count]))

  return (
    <>
      <FormDrawer
        open={open}
        size="lg"
        formId="campaign-form"
        icon={Fish}
        title={cloning ? S.cloneTitle : (campaign ? S.edit : S.addNew)}
        subtitle={cloning ? S.cloneHint : S.title}
        submitLabel={review ? actionLabel : reviewRequired ? S.review.action : S.form.save}
        submitting={saving || checkingAudience}
        cancelLabel={S.close}
        onClose={() => !checkingAudience && onClose?.()}
        onSubmit={submit}
        footer={
          <>
            <Button
              type="submit"
              form="campaign-form"
              busy={saving || checkingAudience}
              disabled={review && values.isActive && !confirmed}
            >
              {review && <ActionIcon size={16} strokeWidth={1.8} />}
              {review ? actionLabel : reviewRequired ? S.review.action : S.form.save}
            </Button>
            {review && <Button variant="soft" onClick={() => setReview(false)}>{S.review.edit}</Button>}
            <Button variant="ghost" onClick={() => !checkingAudience && onClose?.()}>{S.close}</Button>
          </>
        }
      >
        {reviewError && <p role="alert" className="ph__review-error">{reviewError}</p>}
        {review ? (
          <section className="ph__campaign-review" aria-label={S.review.title}>
            <div className={`ph__review-decision ph__review-decision--${launchAction}`}>
              <ActionIcon size={22} strokeWidth={1.8} aria-hidden="true" />
              <div><strong>{actionLabel}</strong><span>{S.review.intent[launchAction]}</span></div>
            </div>

            <div className="ph__review-section">
              <div className="ph__review-heading"><Users size={18} /><h3>{S.review.audienceTitle}</h3></div>
              <div className="ph__review-audience">
                <div><strong>{audience?.Total ?? 0}</strong><span>{S.review.recipientCount}</span></div>
                <div><strong>{values.groupIds.length}</strong><span>{S.review.groupCount}</span></div>
              </div>
              <ul className="ph__review-groups">
                {values.groupIds.map(id => (
                  <li key={id}><span>{groups.find(group => String(group.value) === String(id))?.label || id}</span><strong>{groupCounts.get(Number(id)) ?? 0}</strong></li>
                ))}
              </ul>
              <p className="ph__review-note">{S.review.overlap} {S.review.audienceEstimate}</p>
              {values.isActive && audience?.License && (
                <p className="ph__review-note" role="status">{S.review.enrollment
                  .replace('{existing}', audience.License.AlreadyEnrolled)
                  .replace('{new}', audience.License.NewSeats)
                  .replace('{remaining}', audience.License.RemainingSeats ?? S.review.unlimited)}</p>
              )}
            </div>

            <div className="ph__review-section">
              <div className="ph__review-heading"><Fish size={18} /><h3>{S.review.contentTitle}</h3></div>
              <dl className="ph__review-facts">
                <div><dt>{S.form.name}</dt><dd>{values.name}</dd></div>
                <div><dt>{S.form.template}</dt><dd>{selectedTemplate?.label}</dd></div>
                <div><dt>{S.form.sender}</dt><dd>{senders.find(item => String(item.value) === String(values.senderId))?.label}</dd></div>
                <div><dt>{S.form.url}</dt><dd dir="ltr">{values.url}</dd></div>
                <div><dt>{S.form.program}</dt><dd>{programs.find(item => String(item.value) === String(values.programId))?.label || S.form.programPlaceholder}</dd></div>
              </dl>
              {selectedTemplate && (
                <Button type="button" variant="soft" size="sm" onClick={() => setPreviewOpen(true)}>
                  <Eye size={15} strokeWidth={2} />{S.review.previewTemplate}
                </Button>
              )}
            </div>

            <div className="ph__review-section">
              <div className="ph__review-heading"><CalendarClock size={18} /><h3>{S.review.timingTitle}</h3></div>
              <dl className="ph__review-facts">
                <div><dt>{S.form.startDate}</dt><dd dir="ltr">{values.startDate.replace('T', ' ')}</dd></div>
                <div><dt>{S.form.endDate}</dt><dd dir="ltr">{values.endDate.replace('T', ' ')}</dd></div>
                {/* The chosen sentence reads better here than the instant it works out to,
                    which the admin never typed and would have to decode. */}
                <div>
                  <dt>{S.form.schedule}</dt>
                  <dd><bdi>{sendWindowOptions.find((o) => o.value === values.sendWindow)?.label}</bdi></dd>
                </div>
              </dl>
              {sendEmailsBy && <p className="ph__review-note">{S.review.sendWindow}</p>}
            </div>

            {values.isActive && (
              <label className="ph__review-confirm">
                <input type="checkbox" checked={confirmed} onChange={(event) => {
                  setConfirmed(event.target.checked)
                  setReviewError('')
                }} />
                <span><strong>{S.review.confirmTitle}</strong><small>{S.review.confirm[launchAction]}</small></span>
              </label>
            )}
          </section>
        ) : (
        <FormBody>
          <FormSection>
            <FormCols>
              <FormRow className="form-span" label={S.form.name} error={errors.name}>
                {(id) => (
                  <TextField
                    id={id}
                    value={values.name}
                    onChange={set('name')}
                    invalid={!!errors.name}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow label={S.form.template} error={errors.templateId}>
                {(id) => (
                  <div className="ph__template-select-row">
                    <div className="ph__template-select-field">
                      <Select
                        id={id}
                        value={values.templateId}
                        onChange={set('templateId')}
                        options={templates}
                        placeholder={S.form.templatePlaceholder}
                      />
                    </div>
                    {selectedTemplate && (
                      <Button
                        type="button"
                        variant="soft"
                        size="sm"
                        className="ph__template-preview-btn"
                        onClick={() => setPreviewOpen(true)}
                      >
                        <Eye size={15} strokeWidth={2} />
                        <span>{S.form.previewTemplate || 'معاينة القالب'}</span>
                      </Button>
                    )}
                  </div>
                )}
              </FormRow>

              <FormRow label={S.form.url} error={errors.url}>
                {(id) => (
                  <Select
                    id={id}
                    value={values.url}
                    onChange={set('url')}
                    options={domains}
                    placeholder={S.form.urlPlaceholder}
                  />
                )}
              </FormRow>

              <FormRow label={S.form.sender} error={errors.senderId}>
                {(id) => (
                  <Select
                    id={id}
                    value={values.senderId}
                    onChange={set('senderId')}
                    options={senders}
                    placeholder={S.form.senderPlaceholder}
                  />
                )}
              </FormRow>

              <FormRow label={S.form.program}>
                {(id) => (
                  <Select
                    id={id}
                    value={values.programId}
                    onChange={set('programId')}
                    options={programs}
                    placeholder={S.form.programPlaceholder}
                  />
                )}
              </FormRow>
            </FormCols>
          </FormSection>

          <FormSection>
            <FormCols>
              <FormRow label={S.form.startDate} error={errors.startDate}>
                {(id) => (
                  <DateTimePicker
                    id={id}
                    value={values.startDate}
                    onChange={set('startDate')}
                    max={values.endDate || undefined}
                  />
                )}
              </FormRow>

              <FormRow label={S.form.endDate} error={errors.endDate ?? dateError}>
                {(id) => (
                  <DateTimePicker
                    id={id}
                    value={values.endDate}
                    onChange={set('endDate')}
                    min={values.startDate || undefined}
                  />
                )}
              </FormRow>

              <FormRow label={S.form.schedule} hint={S.form.schedulingHint} error={errors.sendWindow}>
                {(id) => (
                  <Select
                    id={id}
                    value={values.sendWindow}
                    onChange={set('sendWindow')}
                    options={sendWindowOptions}
                  />
                )}
              </FormRow>
            </FormCols>
          </FormSection>

          <FormSection>
            <FormCols>
              <FormSpan>
                <GroupPicker
                  options={groups}
                  selected={values.groupIds}
                  onChange={set('groupIds')}
                  legend={S.form.groups}
                  error={errors.groupIds}
                  selectAllLabel={S.form.selectAll}
                  filterLabel={S.form.filter}
                />
              </FormSpan>
            </FormCols>
          </FormSection>

          <SwitchField
            checked={values.isActive}
            onChange={set('isActive')}
            label={S.form.active}
          />
        </FormBody>
        )}
      </FormDrawer>

      {previewOpen && selectedTemplate && (
        <TemplatePreview
          template={selectedTemplate}
          surface={previewSurface}
          onSurfaceChange={setPreviewSurface}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  )
}
